import { type Dispatch, type MouseEvent, type SetStateAction, useCallback, useMemo, useState } from 'react';
import type { UseFieldArrayUpdate } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import type {
  AudienceForm,
  AudienceListType,
  FormSegment,
  GroupFieldName,
} from '../../../../../../../../modules/audiences/AudienceForm/types';
import { ConfirmationModal } from '../../../../ConfirmationModal';
import type { SelectedLocationSegment } from '../../../constants';
import { useLocationSection } from '../../../hooks/useLocationSection';
import { BaseFilterSection } from '../BaseFilterSection';
import { LocationExpanded } from './LocationExpanded';

export type SegmentSetters = {
  state: Dispatch<SetStateAction<FormSegment[]>>;
  zip: Dispatch<SetStateAction<FormSegment[]>>;
  dma: Dispatch<SetStateAction<FormSegment[]>>;
  congressional: Dispatch<SetStateAction<FormSegment[]>>;
};

export type Segments = {
  state: FormSegment[];
  zip: FormSegment[];
  dma: FormSegment[];
  congressional: FormSegment[];
};

export type LocationSectionProps = {
  isEdit: boolean;
  groupFieldName: GroupFieldName;
  listType: AudienceListType;
  update: UseFieldArrayUpdate<
    AudienceForm,
    'normalized_definition.includes.groups' | 'normalized_definition.excludes.groups'
  >;
  index: number;
  isExpanded: boolean;
  onToggleExpand: () => void;
  selectedLocationSegment: SelectedLocationSegment;
  setSelectedLocationSegment: (segment: SelectedLocationSegment) => void;
};

export const LocationSection = ({
  isEdit,
  groupFieldName,
  listType,
  update,
  index,
  isExpanded,
  onToggleExpand,
  selectedLocationSegment,
  setSelectedLocationSegment,
}: LocationSectionProps) => {
  const { t } = useTranslation();
  const [showClearModal, setShowClearModal] = useState(false);

  const {
    locationStateSegments,
    locationZipCodeSegments,
    locationDmaSegments,
    locationCongressionalSegments,
    setLocationStateSegments,
    setLocationZipCodeSegments,
    setLocationDmaSegments,
    setLocationCongressionalSegments,
    numberOfLocationItemsSelected,
    clearAllLocation,
  } = useLocationSection({
    groupFieldName,
    update,
    index,
  });

  const handleClearButtonClick = useCallback((event: MouseEvent<HTMLButtonElement>) => {
    event.stopPropagation();

    setShowClearModal(true);
  }, []);

  const handleClearConfirm = useCallback(() => {
    clearAllLocation();
    setShowClearModal(false);
    onToggleExpand();
  }, [clearAllLocation, onToggleExpand]);

  const handleClearCancel = useCallback(() => {
    setShowClearModal(false);
  }, []);

  const segmentSetters: SegmentSetters = useMemo(
    () => ({
      state: setLocationStateSegments,
      zip: setLocationZipCodeSegments,
      dma: setLocationDmaSegments,
      congressional: setLocationCongressionalSegments,
    }),
    [setLocationStateSegments, setLocationZipCodeSegments, setLocationDmaSegments, setLocationCongressionalSegments]
  );

  const segments: Segments = useMemo(
    () => ({
      state: locationStateSegments,
      zip: locationZipCodeSegments,
      dma: locationDmaSegments,
      congressional: locationCongressionalSegments,
    }),
    [locationStateSegments, locationZipCodeSegments, locationDmaSegments, locationCongressionalSegments]
  );

  return (
    <BaseFilterSection
      titleKey="User Location"
      dataTestId="localization-section"
      selectedCount={numberOfLocationItemsSelected}
      onClear={handleClearButtonClick}
      showClearButton={isEdit && !!numberOfLocationItemsSelected}
      expandableWhenEmpty
      isExpanded={isExpanded}
      onToggleExpand={onToggleExpand}
    >
      <LocationExpanded
        isEdit={isEdit}
        listType={listType}
        selectedLocationSegment={selectedLocationSegment}
        setSelectedLocationSegment={setSelectedLocationSegment}
        segmentSetters={segmentSetters}
        segments={segments}
      />

      <ConfirmationModal
        isOpen={showClearModal}
        onClose={handleClearCancel}
        onDelete={handleClearConfirm}
        title={t('Clear all user location data')}
        item={t('All user location data')}
      />
    </BaseFilterSection>
  );
};
